import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { ScoreGauge } from "../../components/ScoreGauge";
import { CompetencyRadar } from "../../components/CompetencyRadar";
import { levelMeta, COMPETENCY_LABELS, scoreColor } from "../../lib/scoring";
import {
  LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid,
} from "recharts";
import {
  Target, ArrowRight, TrendingUp, Award, CalendarClock, Sparkles, ChevronRight,
  Video, Phone, UserCheck,
} from "lucide-react";

const Card = ({ children, className = "", ...p }) => (
  <div className={`bg-white rounded-2xl border border-slate-200 shadow-card ${className}`} {...p}>{children}</div>
);

function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white rounded-lg shadow-lift border border-slate-200 px-3 py-2 text-xs">
      <div className="font-bold text-ink num">{payload[0].value} / 100</div>
    </div>
  );
}

export default function CandidateDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [intel, setIntel] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get("/candidate/dashboard").then((r) => setData(r.data)).catch(() => setErr(true));
    api.get("/candidate/intelligence").then((r) => setIntel(r.data)).catch(() => {});
  }, []);

  if (err) return <div className="text-ink-soft">Could not load dashboard.</div>;
  if (!data) return <DashboardSkeleton />;

  const meta = levelMeta(data.readiness);
  const cand = data.candidate;
  const result = data.result;
  const cmp = data.comparison;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-brand mb-1">Candidate Dashboard</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-ink">Welcome back, {cand.name.split(" ")[0]}</h1>
          <p className="text-ink-soft mt-1 text-sm">Target role: <span className="font-semibold text-ink">{cand.target_role_name}</span></p>
        </div>
        <button data-testid="book-interview-cta" onClick={() => navigate("/candidate/book")}
          className="inline-flex items-center gap-2 text-sm font-bold text-white bg-brand hover:bg-brand-hover px-5 py-3 rounded-full shadow-ambient hover:-translate-y-0.5 transition-transform">
          <Target className="h-4 w-4" /> Start New Interview
        </button>
      </div>

      {/* Top row */}
      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="p-6 flex flex-col items-center justify-center text-center" data-testid="readiness-card">
          <div className="text-xs font-bold uppercase tracking-wider text-ink-faint mb-3">Einstein Score</div>
          <ScoreGauge value={data.readiness} size={210} testid="readiness-gauge" />
          <p className="text-xs text-ink-soft mt-4 max-w-[240px] leading-relaxed">
            Your Einstein Score measures your current preparation for your selected target role.
          </p>
        </Card>

        <Card className="lg:col-span-2 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="font-heading font-bold text-ink flex items-center gap-2"><TrendingUp className="h-4 w-4 text-brand" /> Score History</div>
            {cmp && (
              <span className="text-xs font-bold px-2.5 py-1 rounded-full num" style={{ background: "#D1FAE5", color: "#065F46" }}>
                {cmp.improvement >= 0 ? "+" : ""}{cmp.improvement} pts vs last
              </span>
            )}
          </div>
          {data.history.length ? (
            <ResponsiveContainer width="100%" height={230}>
              <LineChart data={data.history} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                <XAxis dataKey="n" tickFormatter={(n) => `Interview ${n}`} tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis domain={[0, 100]} tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip />} />
                <Line type="monotone" dataKey="overall" stroke="#0055FF" strokeWidth={3} dot={{ r: 5, fill: "#0055FF", strokeWidth: 2, stroke: "#fff" }} activeDot={{ r: 7 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState onClick={() => navigate("/candidate/book")} />
          )}
        </Card>
      </div>

      {/* Practice Interview */}
      <div>
        <div className="font-heading font-bold text-ink mb-3">Practice Interview</div>
        <div className="grid sm:grid-cols-3 gap-4">
          {[
            { mode: "video", icon: Video, emoji: "🎥", title: "AI Video Interview", desc: "Practice face-to-face with AI.", cta: "Start Video", accent: "bg-brand-light text-brand" },
            { mode: "voice", icon: Phone, emoji: "📞", title: "AI Voice Interview", desc: "Practice through a phone call.", cta: "Start Voice", accent: "bg-emerald-100 text-emerald-600" },
            { mode: "human", icon: UserCheck, emoji: "👤", title: "Human Expert Interview", desc: "Practice with an industry professional.", cta: "Start Expert", accent: "bg-slate-900 text-white" },
          ].map((c) => {
            const Icon = c.icon;
            return (
              <Card key={c.mode} className="p-5 flex flex-col">
                <span className={`h-11 w-11 rounded-xl grid place-items-center mb-3 ${c.accent}`}><Icon className="h-5 w-5" /></span>
                <div className="font-heading font-bold text-ink">{c.emoji} {c.title}</div>
                <p className="text-sm text-ink-soft mt-1 mb-4 flex-1">{c.desc}</p>
                <button data-testid={`practice-${c.mode}`} onClick={() => navigate(`/candidate/book?mode=${c.mode}`)}
                  className="w-full text-sm font-bold text-white bg-brand hover:bg-brand-hover px-4 py-2.5 rounded-full transition-colors inline-flex items-center justify-center gap-1">
                  {c.cta} <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Next Best Action + Action Center (Phase 2 intelligence) */}
      {intel?.has_data && (
        <div className="grid lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 p-6 bg-slate-900 text-white border-slate-900" data-testid="next-action-banner">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-blue-300 mb-2"><Sparkles className="h-4 w-4" /> Your Next Best Action</div>
            <div className="font-heading text-xl font-bold">{intel.next_action.title}</div>
            <p className="text-slate-300 text-sm mt-1">{intel.next_action.detail}</p>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button onClick={() => navigate("/candidate/readiness")} data-testid="go-readiness"
                className="inline-flex items-center gap-2 text-sm font-bold text-white bg-brand hover:bg-brand-hover px-5 py-2.5 rounded-full transition-colors">
                Open Readiness Profile <ChevronRight className="h-4 w-4" />
              </button>
              {intel.next_action.expected_gain > 0 && <span className="text-xs num font-bold text-emerald-300">Expected +{intel.next_action.expected_gain} readiness pts</span>}
            </div>
          </Card>
          <Card className="p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="font-heading font-bold text-ink flex items-center gap-2"><Award className="h-4 w-4 text-amber-500" /> Action Center</div>
              <span className="text-xs num font-bold text-brand">{intel.action_center.completed}/{intel.action_center.total}</span>
            </div>
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden mb-3"><div className="h-full rounded-full bg-brand" style={{ width: `${(intel.action_center.completed / intel.action_center.total) * 100}%` }} /></div>
            <div className="space-y-1.5">
              {intel.action_center.tasks.slice(0, 3).map((t) => (
                <div key={t.id} className="flex items-center gap-2 text-sm text-ink-soft"><span className={`h-1.5 w-1.5 rounded-full ${t.completed ? "bg-emerald-500" : "bg-slate-300"}`} /> {t.title}</div>
              ))}
            </div>
            <button onClick={() => navigate("/candidate/readiness")} className="mt-3 text-xs font-bold text-brand hover:underline">View all →</button>
          </Card>
        </div>
      )}

      {result ? (
        <>
          {/* competency + radar */}
          <div className="grid lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2 p-6">
              <div className="font-heading font-bold text-ink mb-4">Competency Breakdown</div>
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
                {Object.entries(COMPETENCY_LABELS).map(([k, label]) => {
                  const v = result.competencies[k];
                  return (
                    <div key={k}>
                      <div className="flex justify-between text-sm mb-1.5">
                        <span className="font-medium text-ink-soft">{label}</span>
                        <span className="num font-bold text-ink">{v}</span>
                      </div>
                      <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${v}%`, background: scoreColor(v) }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
            <Card className="p-6">
              <div className="font-heading font-bold text-ink mb-2">Skill Radar</div>
              <CompetencyRadar competencies={result.competencies} height={260} />
            </Card>
          </div>

          {/* skill gaps + improvement + courses preview */}
          <div className="grid lg:grid-cols-3 gap-6">
            <Card className="p-6">
              <div className="font-heading font-bold text-ink mb-4">Top Skill Gaps</div>
              <div className="space-y-3.5">
                {result.skill_gaps.slice(0, 4).map((g) => (
                  <div key={g.skill}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-semibold text-ink">{g.label}</span>
                      <span className="num text-xs font-bold text-red-500">-{g.gap}</span>
                    </div>
                    <div className="relative h-2 rounded-full bg-slate-100">
                      <div className="absolute h-full rounded-full bg-brand" style={{ width: `${g.current}%` }} />
                      <div className="absolute top-1/2 -translate-y-1/2 h-3 w-0.5 bg-emerald-500" style={{ left: `${g.target}%` }} />
                    </div>
                  </div>
                ))}
                {!result.skill_gaps.length && <div className="text-sm text-ink-soft">No gaps — you're interview ready! 🎯</div>}
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="font-heading font-bold text-ink">Improvement Plan</div>
              </div>
              <div className="space-y-3">
                {result.improvement_plan.map((p) => (
                  <div key={p.priority} className="rounded-xl border border-slate-200 p-3.5">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-brand">Priority {p.priority}</span>
                      <span className="text-[11px] num font-semibold text-emerald-600">{p.estimated_improvement} pts</span>
                    </div>
                    <div className="font-semibold text-ink text-sm mt-1">{p.label}</div>
                    <div className="text-xs text-ink-faint num">{p.current} → {p.target}</div>
                  </div>
                ))}
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="font-heading font-bold text-ink flex items-center gap-2"><Sparkles className="h-4 w-4 text-brand" /> Recommended</div>
              </div>
              <div className="space-y-3">
                {result.recommended_courses.slice(0, 3).map((c) => (
                  <div key={c.id} className="rounded-xl border border-slate-200 p-3.5 hover:border-brand transition-colors">
                    <div className="text-sm font-semibold text-ink leading-tight">{c.title}</div>
                    <div className="text-xs text-ink-faint mt-1">{c.provider} · {c.duration} · ⭐ {c.rating}</div>
                    <div className="text-[11px] text-brand font-semibold mt-1">For {c.skill_label}</div>
                  </div>
                ))}
              </div>
              <button onClick={() => navigate(`/candidate/result/${result.interview.id}`)}
                data-testid="view-full-report" className="mt-4 w-full text-sm font-bold text-brand hover:bg-brand-light px-4 py-2.5 rounded-full transition-colors flex items-center justify-center gap-1">
                View Full Report <ChevronRight className="h-4 w-4" />
              </button>
            </Card>
          </div>
        </>
      ) : null}

      {/* Upcoming */}
      {data.upcoming?.length > 0 && (
        <Card className="p-6">
          <div className="font-heading font-bold text-ink mb-4 flex items-center gap-2"><CalendarClock className="h-4 w-4 text-brand" /> Upcoming Interviews</div>
          <div className="space-y-2">
            {data.upcoming.map((iv) => {
              const mode = iv.mode || (iv.type === "ai" ? "video" : "human");
              const icon = iv.mode_icon || (mode === "voice" ? "📞" : mode === "human" ? "👤" : "🎥");
              const label = iv.mode_label || (mode === "voice" ? "AI Voice Interview" : mode === "human" ? "Human Expert Interview" : "AI Video Interview");
              return (
                <div key={iv.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 p-4">
                  <div>
                    <div className="font-semibold text-ink text-sm">{iv.role_name} · {icon} {label}</div>
                    <div className="text-xs text-ink-faint num">{iv.date} · {iv.time} · {iv.panel_name}</div>
                    {mode === "voice" && <div className="text-[11px] font-semibold text-emerald-600 mt-0.5">Mock2Crack will call you</div>}
                  </div>
                  <button onClick={() => navigate(`/candidate/interview/${iv.id}`)} data-testid={`attend-${iv.id}`}
                    className="text-xs font-bold text-white bg-brand hover:bg-brand-hover px-4 py-2 rounded-full transition-colors inline-flex items-center gap-1">
                    {mode === "voice" ? "View Details" : "Join Interview"} <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}

function EmptyState({ onClick }) {
  return (
    <div className="h-[230px] grid place-items-center text-center">
      <div>
        <div className="h-14 w-14 rounded-2xl bg-brand-light grid place-items-center mx-auto mb-3"><Award className="h-6 w-6 text-brand" /></div>
        <div className="font-semibold text-ink">No interviews yet</div>
        <p className="text-sm text-ink-soft mt-1 mb-4">Take your first mock interview to see your readiness.</p>
        <button onClick={onClick} className="text-sm font-bold text-white bg-brand px-5 py-2.5 rounded-full">Start Interview</button>
      </div>
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="h-10 w-64 bg-slate-200 rounded-lg" />
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="h-72 bg-white rounded-2xl border border-slate-200" />
        <div className="lg:col-span-2 h-72 bg-white rounded-2xl border border-slate-200" />
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        {[1, 2, 3].map((i) => <div key={i} className="h-56 bg-white rounded-2xl border border-slate-200" />)}
      </div>
    </div>
  );
}
